import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material'
import SignatureCanvas from 'react-signature-canvas'
import { format } from 'date-fns'
import { User } from '@/types'
import { ConsentService } from '@/services/consentService'
import { fetchUserIpAddress } from '@/utils/ipAddress'
import LOGO from '@/assets/pillarThera.png'

interface HippaConsentDialogProps {
  open: boolean
  user: User
  onConsentSubmitted: () => void
}

const HIPAA_CONSENT_TEXT = {
  title: 'PillarThera HIPAA Compliance Acknowledgement',
  introduction:
    'As a user of the PillarThera Platform with access to Protected Health Information (PHI), you are required to acknowledge and comply with the following obligations under HIPAA:',
  sections: [
    {
      title: '1. Confidentiality',
      content:
        'You agree to maintain the confidentiality of all patient information in accordance with the Health Insurance Portability and Accountability Act (HIPAA).',
    },
    {
      title: '2. Access Control',
      content:
        'You agree to use your own unique login credentials and never share them with anyone else.',
    },
    {
      title: '3. Data Security',
      content:
        'You agree not to download, export, or share patient data outside the PillarThera Platform unless specifically authorized.',
    },
    {
      title: '4. Breach Reporting',
      content:
        'You agree to immediately report any suspected data breach, unauthorized access, or security incident to the PillarThera compliance team.',
    },
    {
      title: '5. Acknowledgement',
      content:
        'By selecting "I Agree" or continuing into the system, you confirm that you understand and accept your responsibilities under HIPAA and will comply with all applicable privacy and security requirements.',
    },
  ],
}

const HippaConsentDialog: React.FC<HippaConsentDialogProps> = ({
  open,
  user,
  onConsentSubmitted,
}) => {
  const signaturePadRef = useRef<SignatureCanvas>(null)
  const [hasReadAndAgreed, setHasReadAndAgreed] = useState(false)
  const [signatureData, setSignatureData] = useState<string | null>(null)
  const [ipAddress, setIpAddress] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFetchingIp, setIsFetchingIp] = useState(true)

  const staffFullName = `${user.firstName} ${user.lastName}`
  const dateOfConsent = format(new Date(), 'MM/dd/yyyy')

  useEffect(() => {
    if (open) {
      // Fetch IP address when dialog opens
      fetchUserIpAddress()
        .then(ip => {
          setIpAddress(ip)
          setIsFetchingIp(false)
        })
        .catch(() => {
          setIpAddress('Unknown')
          setIsFetchingIp(false)
        })
    }
  }, [open])

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear()
      setSignatureData(null)
    }
  }

  const handleSignatureEnd = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const dataURL = signaturePadRef.current.toDataURL('image/png')
      setSignatureData(dataURL)
    } else {
      setSignatureData(null)
    }
  }

  const handleSubmit = async () => {
    if (!signatureData) {
      setError('Please provide a signature')
      return
    }

    if (!hasReadAndAgreed) {
      setError('Please confirm that you have read and agreed to the terms')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Hardcoded values as per requirements
      const agreementTemplateId = '686e3101582277712b6bb98c'
      const agreementVersion = 'v1.0'
      const signatureType = 'drawn'

      // Get user agent from browser
      const userAgent = navigator.userAgent

      await ConsentService.submitHippaConsent({
        userId: user.id,
        agreementTemplateId,
        agreementVersion,
        fullName: staffFullName,
        ipAddress,
        userAgent,
        consentTime: new Date().toISOString(),
        signatureType,
        drawnSignature: signatureData, // This is already a data URL (data:image/png;base64,...)
      })

      onConsentSubmitted()
    } catch (err: any) {
      console.error('Error submitting consent:', err)
      setError(
        err.response?.data?.message ||
          'Failed to submit consent. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const canSubmit = hasReadAndAgreed && signatureData !== null && !loading

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing via backdrop click or ESC key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return
        }
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogContent
        dividers
        sx={{
          maxHeight: 'calc(90vh - 100px)',
          overflowY: 'auto',
          p: 0,
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pt: 4,
            pb: 3,
            bgcolor: 'grey.50',
          }}
        >
          <Box
            component="img"
            src={LOGO}
            alt="PillarThera Logo"
            sx={{
              height: 60,
              width: 'auto',
            }}
          />
        </Box>

        {/* Main Content */}
        <Box sx={{ p: 4 }}>
          {/* Title */}
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              mb: 2,
              color: 'primary.main',
              textAlign: 'center',
            }}
          >
            {HIPAA_CONSENT_TEXT.title}
          </Typography>

          {/* Introduction */}
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: 'text.primary',
              lineHeight: 1.7,
              fontSize: '1rem',
            }}
          >
            {HIPAA_CONSENT_TEXT.introduction}
          </Typography>

          {/* Consent Sections */}
          <Box sx={{ mb: 4 }}>
            {HIPAA_CONSENT_TEXT.sections.map((section, index) => (
              <Box
                key={index}
                sx={{
                  mb: 3,
                  pb: 3,
                  borderBottom:
                    index < HIPAA_CONSENT_TEXT.sections.length - 1
                      ? '1px solid'
                      : 'none',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    mb: 1.5,
                    color: 'text.primary',
                    fontSize: '1.125rem',
                  }}
                >
                  {section.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    pl: 0,
                  }}
                >
                  {section.content}
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Checkbox */}
          <Box
            sx={{
              mb: 4,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasReadAndAgreed}
                  onChange={e => {
                    setHasReadAndAgreed(e.target.checked)
                    setError(null)
                  }}
                  sx={{
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  I confirm that I have read, understood, and agreed to the
                  above terms.
                </Typography>
              }
            />
          </Box>

          {/* Signature Pad */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ mb: 2, color: 'text.primary' }}
            >
              Digital Signature
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please sign below:
            </Typography>
            <Paper
              elevation={0}
              sx={{
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                mb: 2,
                bgcolor: 'white',
                display: 'flex',
                justifyContent: 'center',
                overflow: 'auto',
                '& canvas': {
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  cursor: 'crosshair',
                  maxWidth: '100%',
                  height: 'auto',
                },
              }}
            >
              <SignatureCanvas
                ref={signaturePadRef}
                canvasProps={{
                  width: 700,
                  height: 200,
                  className: 'signature-canvas',
                  style: { maxWidth: '100%', height: 'auto' },
                }}
                onEnd={handleSignatureEnd}
              />
            </Paper>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearSignature}
              sx={{ textTransform: 'none' }}
            >
              Clear Signature
            </Button>
          </Box>

          {/* Consent Details */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ mb: 3, color: 'text.primary' }}
            >
              Consent Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      mb: 0.5,
                    }}
                  >
                    Staff Full Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {staffFullName}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      mb: 0.5,
                    }}
                  >
                    Date of Consent
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {dateOfConsent}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      mb: 0.5,
                    }}
                  >
                    IP Address
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {isFetchingIp ? (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CircularProgress size={14} />
                        <span>Loading...</span>
                      </Box>
                    ) : (
                      ipAddress
                    )}
                  </Typography>
                </Box>
              </Grid>
              {signatureData && (
                <Grid item xs={12}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      Signature
                    </Typography>
                    <Box
                      component="img"
                      src={signatureData}
                      alt="Signature"
                      sx={{
                        maxWidth: 400,
                        maxHeight: 120,
                        border: '2px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 1,
                        bgcolor: 'white',
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          px: 4,
          bgcolor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          size="large"
          sx={{
            minWidth: 150,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'I Agree'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default HippaConsentDialog
